/**
 * Das Nachrichtenfenster (Migration 168): Gespräche mit Kunden, Leeds und
 * Lieferanten, gelesen aus demselben Speicher wie der Schriftwechsel am
 * Projekt. Antworten, Zuweisen und Ausblenden laufen über `usePost`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";

import type { PostAnhang } from "./usePost";

export type NachrichtArt = "kunde" | "leed" | "lieferant";
export type NachrichtFilter = "alle" | NachrichtArt;

/** Ein Chat: alle Gespräche mit einem Kunden, Leed oder Lieferanten. */
export type FadenKurz = {
  /** „kunde:<id>", „leed:<id>", „lieferant:<id>" oder „faden:<gespräch>". */
  schluessel: string;
  art: NachrichtArt;
  titel: string;
  betreff: string;
  vorschau: string;
  letzte: string;
  letzte_von_uns: boolean;
  /** Eingehende Nachrichten, die dieses Konto noch nicht gesehen hat. */
  neu: number;
  postfach: string;
  kunde_id: string;
  projekt_id: string;
  projekt_titel: string;
  /** true = das Projekt ist ein Vorschlag des Imports. */
  vorschlag: boolean;
  anfrage_id: string;
  lieferant_id: string;
  anzahl: number;
  /** Die Gmail-Gespräche des Chats. */
  faeden: string[];
};

/** Ein Gmail-Gespräch im Chat — eigener Betreff, eigenes Projekt, eigene Antwortadresse. */
export type Gespraech = {
  schluessel: string; betreff: string; projekt_id: string; projekt_titel: string; vorschlag: boolean;
  antwort_auf: string; an: string; postfach: string; letzte: string; neu: number;
};

export type Blase = {
  id: string;
  gespraech: string;
  richtung: "in" | "out";
  absender: string;
  von: string;
  zeitpunkt: string;
  betreff: string;
  /** Ohne zitierten Verlauf und lange Signatur. */
  text: string;
  /** Die ganze Mail — nur gesetzt, wenn `text` gekürzt ist. */
  voll: string;
  anhaenge: PostAnhang[];
  neu: boolean;
  /** Lieferantenpost, die nicht in die Liste gehört („Rechnung", „Automatisch") — im Chat leiser gezeigt. */
  leise: string;
};

export type ProjektWahl = { id: string; titel: string; zusatz: string; grund: string; erledigt: boolean };

export type LeedKurz = {
  id: string; name: string; email: string; telefon: string; strasse: string; plz: string; ort: string;
  kundentyp: string; betreff: string; anliegen: string; kunde_id: string;
};

export type Faden = FadenKurz & {
  /** Wem eine Antwort gilt (neuestes Gespräch, sonst die Stammadresse); leer = unbekannt. */
  an: string;
  /** connect_message.id, auf die geantwortet wird; leer = noch keine Post. */
  antwort_auf: string;
  /** Älteste zuerst. */
  gespraeche: Gespraech[];
  nachrichten: Blase[];
  /** Wohin das Gespräch gehören kann — bei Lieferanten die wahrscheinlichen zuerst. */
  projekte: ProjektWahl[];
  leed: LeedKurz | null;
};

export type NachrichtenListe = { faeden: FadenKurz[]; neu: Record<NachrichtFilter, number> };

function abfrage(teile: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(teile)) if (v) p.set(k, v);
  return p.toString();
}

export function useNachrichtenListe(
  { wer, art, kundeId, q }: { wer: string; art: NachrichtFilter; kundeId?: string; q?: string },
  aktiv = true,
) {
  return useQuery<NachrichtenListe>({
    queryKey: ["nachrichten", "liste", wer, art, kundeId || "", q || ""],
    queryFn: () => apiGet<NachrichtenListe>(`/api/kontor/nachrichten?${abfrage({ wer, art, kunde_id: kundeId, q })}`),
    enabled: aktiv,
    staleTime: 20_000,
    refetchInterval: aktiv ? 60_000 : false,
  });
}

/** `aktiv = false`: das Fenster ist zu — die Daten bleiben stehen, es wird nur nicht nachgeladen. */
export function useFaden(schluessel?: string, aktiv = true) {
  return useQuery<Faden>({
    queryKey: ["nachrichten", "faden", schluessel],
    queryFn: () => apiGet<Faden>(`/api/kontor/nachrichten/faden?${abfrage({ s: schluessel })}`),
    enabled: !!schluessel && aktiv,
    staleTime: 10_000,
    refetchInterval: schluessel && aktiv ? 60_000 : false,
  });
}

/** Die Zahl an der Sprechblase — alle Minute und wenn das Fenster wieder in den Vordergrund kommt. */
export function useNachrichtenAnzahl() {
  return useQuery<{ meine: number; alle: number }>({
    queryKey: ["nachrichten", "anzahl"],
    queryFn: () => apiGet<{ meine: number; alle: number }>("/api/kontor/nachrichten/anzahl"),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useGelesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schluessel: string[]) => apiPost<{ ok: boolean }>("/api/kontor/nachrichten/gelesen", { schluessel }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nachrichten", "anzahl"] });
      qc.invalidateQueries({ queryKey: ["nachrichten", "liste"] });
    },
  });
}
